if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      clampQuantityToStock() {
        const qtyInput = this.form?.querySelector('[name="quantity"]');
        if (!qtyInput?.dataset.tcgStockCap) return;
        const stock = parseInt(qtyInput.dataset.tcgStockCap, 10);
        if (Number.isNaN(stock) || stock < 0) return;
        const cartQty = parseInt(qtyInput.dataset.cartQuantity || '0', 10) || 0;
        const max = Math.max(0, stock - cartQty);
        const min = parseInt(qtyInput.min || '1', 10) || 1;
        let value = parseInt(qtyInput.value, 10);
        if (Number.isNaN(value)) value = min;
        if (value > max) value = max;
        if (value < min) value = Math.min(min, max);
        qtyInput.value = value;
        qtyInput.max = max;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.clampQuantityToStock();
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;
        const reserveApi = window.Kl24InventoryReservation;
        const useReserve = reserveApi?.shouldReserveElement?.(this);
        let reserved = false;

        const finish = () => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          CartPerformance.measureFromEvent('add:user-action', evt);
        };

        const run = async () => {
          if (useReserve) {
            try {
              const reserveResult = await reserveApi.reserve({
                shopifyVariantId: variantId,
                quantity,
                syncShopify: false,
              });
              if (!reserveResult?.ok) {
                const catalogMiss =
                  reserveResult?.error === 'not_found' ||
                  reserveResult?.skipped ||
                  reserveResult?.degraded;
                if (!catalogMiss) {
                  this.error = true;
                  this.handleErrorMessage(
                    reserveResult?.message ||
                      'Diese Karte ist gerade von einem anderen Besucher reserviert.'
                  );
                  return;
                }
                console.warn(
                  '[KL24 Reserve] Katalog-Miss — Warenkorb ohne Hold:',
                  reserveResult?.message || reserveResult?.error
                );
              } else if (!reserveResult?.degraded && !reserveResult?.skipped) {
                reserved = true;
              }
            } catch (error) {
              console.warn('[KL24 Reserve] Reservierung übersprungen:', error?.message || error);
            }
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          try {
            const response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());

            if (response.status) {
              if (reserved && useReserve) {
                await reserveApi.release({ shopifyVariantId: variantId, syncShopify: false });
                reserved = false;
              }
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (reserved && useReserve) {
              try {
                await reserveApi.reserve({
                  shopifyVariantId: variantId,
                  quantity,
                  syncShopify: false,
                });
              } catch (syncError) {
                console.warn('[KL24 Reserve] Shopify sync:', syncError);
              }
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          } catch (error) {
            if (reserved && useReserve) {
              await reserveApi.release({ shopifyVariantId: variantId, syncShopify: false });
            }
            console.error(error);
            this.error = true;
            this.handleErrorMessage(error.message || 'Fehler beim Hinzufügen.');
          }
        };

        run().finally(finish);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
